"use client"

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useAuthStore } from './auth-store'
import { fetchProfile as fetchRemoteProfile, updateProfile as updateRemoteProfile } from '@/lib/convex-profile'

/**
 * The only fields this store can actually persist.
 *
 * Deliberately not `Partial<ProfileData>`: `name`, `email` and `avatar_url`
 * belong to Clerk and are mirrored in by `users.ensureUser`, so the backend
 * ignores them. Typing this as the full profile let callers pass a `name` that
 * was silently dropped — and because the value arrives as a variable rather
 * than an object literal, TypeScript's excess-property check never fired.
 */
export type ProfileUpdate = {
  has_seen_onboarding?: boolean
  has_seen_notification_prompt?: boolean
}

interface ProfileData {
  id: string
  email: string
  name: string | null
  avatar_url: string | null
  updated_at: string
  // Add other profile fields as needed
  role?: string
  has_seen_onboarding?: boolean
  has_seen_notification_prompt?: boolean
}

interface CachedProfile extends ProfileData {
  _cached_at: number
  _version: string
}

interface ProfileState {
  // State
  profile: ProfileData | null
  isLoading: boolean
  error: string | null
  
  // Actions
  refetchProfile: () => Promise<void>
  updateProfile: (updates: ProfileUpdate) => Promise<void>
  clearCache: () => void
  ensureProfileLoaded: () => Promise<ProfileData | null>
  
  // Internal setters and helpers
  setProfile: (profile: ProfileData | null) => void
  setIsLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  getCacheKey: () => string | null
  isCacheValid: (cachedProfile: CachedProfile) => boolean
  loadFromCache: () => CachedProfile | null
  saveToCache: (profileData: ProfileData) => void
  fetchProfileFromDB: () => Promise<ProfileData | null>
}

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = 'blitztask_profile_'

export const useProfileStore = create<ProfileState>()(
  devtools(
    (set, get) => ({
      // Initial state
      profile: null,
      isLoading: false,
      error: null,

      // Internal setters
      setProfile: (profile) => set({ profile }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      // Get cache key for current user
      getCacheKey: () => {
        const user = useAuthStore.getState().user
        return user?.id ? `${CACHE_KEY_PREFIX}${user.id}` : null
      },

      // Check if cached data is still valid
      isCacheValid: (cachedProfile: CachedProfile): boolean => {
        const now = Date.now()
        const cacheAge = now - cachedProfile._cached_at
        return cacheAge < CACHE_DURATION
      },

      // Load profile from cache
      loadFromCache: (): CachedProfile | null => {
        const cacheKey = get().getCacheKey()
        if (!cacheKey) return null

        try {
          const cached = localStorage.getItem(cacheKey)
          if (!cached) return null

          const cachedProfile: CachedProfile = JSON.parse(cached)
          if (get().isCacheValid(cachedProfile)) {
            return cachedProfile
          } else {
            // Cache expired, remove it
            localStorage.removeItem(cacheKey)
            return null
          }
        } catch (error) {
          console.error('Error loading profile from cache:', error)
          return null
        }
      },

      // Save profile to cache
      saveToCache: (profileData: ProfileData) => {
        const cacheKey = get().getCacheKey()
        if (!cacheKey) return

        try {
          const cachedProfile: CachedProfile = {
            ...profileData,
            _cached_at: Date.now(),
            _version: profileData.updated_at
          }
          localStorage.setItem(cacheKey, JSON.stringify(cachedProfile))
        } catch (error) {
          console.error('Error saving profile to cache:', error)
        }
      },

      // Fetch profile from database
      fetchProfileFromDB: async (): Promise<ProfileData | null> => {
        const user = useAuthStore.getState().user
        if (!user?.id) return null

        try {
          set({ error: null })

          return await fetchRemoteProfile()
        } catch (error) {
          console.error('Error fetching profile from database:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to fetch profile' })
          return null
        }
      },

      refetchProfile: async () => {
        const user = useAuthStore.getState().user
        if (!user) {
          set({ profile: null, isLoading: false })
          return
        }

        set({ isLoading: true, error: null })

        try {
          // First, try to load from cache
          const cachedProfile = get().loadFromCache()
          if (cachedProfile) {
            set({ profile: cachedProfile, isLoading: false })
            return
          }

          // If no cache, fetch from database
          const profileData = await get().fetchProfileFromDB()
          if (profileData) {
            set({ profile: profileData })
            get().saveToCache(profileData)
          }
        } catch (error) {
          console.error('Error in refetchProfile:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to fetch profile' })
        } finally {
          set({ isLoading: false })
        }
      },

      ensureProfileLoaded: async (): Promise<ProfileData | null> => {
        const currentProfile = get().profile
        if (currentProfile) {
          return currentProfile
        }

        // Profile not loaded, try to load it
        await get().refetchProfile()
        return get().profile
      },

      updateProfile: async (updates: ProfileUpdate) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error('User not authenticated')

        // Ensure profile is loaded before updating
        const currentProfile = await get().ensureProfileLoaded()
        
        if (!currentProfile) {
          throw new Error('Failed to load profile. Please try again.')
        }

        try {
          set({ isLoading: true, error: null })

          const data = await updateRemoteProfile(updates)

          // Update local state
          const newProfile = { ...currentProfile, ...data }
          set({ profile: newProfile })

          // Update cache
          get().saveToCache(newProfile)
        } catch (error) {
          console.error('Error updating profile:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to update profile' })
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      clearCache: () => {
        const cacheKey = get().getCacheKey()
        if (cacheKey) {
          localStorage.removeItem(cacheKey)
        }
      }
    }),
    { name: 'profile-store' }
  )
)
